import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Col, Row, message, Typography, Skeleton, Table, notification } from "antd";

import { getTasksDeleted, getTasksDeletedByCode } from "../../../../API/tasks";

import WithPermission from "../../../Common/WithPermission/WithPermission";
import ExportButton from '../../../Common/ExportButton/ExportButton';
import BackButton from "../../../Common/BackButton/BackButton";
import BreadCrumb from "../../../Common/BreadCrumb/BreadCrumb";
import PageTitle from "../../../Common/PageTitle/PageTitle";
import ChangeLog from "../../../Common/ChangeLog/ChangeLog";
import TasksSearch from "../TasksSearch/TasksSearch";
import Columns from "./columns";

import { useAuth } from "../../../Context/AuthContext";

const TasksDeleted = () => {
  const navigate = useNavigate();
  // Permisos
  const { hasPermission, userData } = useAuth();
  // Consulta permisos: Superusuario  
  const isSuperuser = userData?.is_superuser === true;
  // Notificaciones
  const [api, contextHolder] = notification.useNotification();
  // Data
  const [tasksDeleted, setTasksDeleted] = useState(null);
  // Buscador
  const [filteredData, setFilteredData] = useState([]); 
  const [searchText, setSearchText] = useState(''); 

  useEffect(() => {
    const fetchTasksDeleted = async () => {
      try {
        const data = await getTasksDeleted();
        setTasksDeleted(data);
        setFilteredData(data);
      } catch (error) {
        message.error("No se encontraron las tareas eliminados.");
      }
    };

    fetchTasksDeleted();
  }, []);

  const breadCrumbUrl = [
    { label: "Tareas", url: "/tasks" },
    { label: "Eliminadas" },
  ];

  const goToBioFunctions = () => {
    navigate(-1);
  };

  const [changeLogVisible, setChangeLogVisible] = useState(false);
  const [changeLogData, setChangeLogData] = useState(null);
  const [changeLogLogs, setChangeLogLogs] = useState([]);

  const handleOpenChangeLog = async (record) => {
    try {
      const evaluation = await getTasksDeletedByCode(record.code);
      setChangeLogData(record);
      setChangeLogLogs(evaluation.logs || []);
      setChangeLogVisible(true); 
    } catch (error) {
      api.error({ message: "Error al obtener logs", description: error.message });
    }
  };

  const handleCloseChangeLog = () => {
    setChangeLogVisible(false);
    setTimeout(() => {
      setChangeLogData(null);
      setChangeLogLogs([]);
    }, 300);
  };

  const tasksDeletedTableFilter = () => {
    if (!Array.isArray(tasksDeleted)) 
      return { tasksDeleted: [], dependencies: [], types: [], statuses: [], areas: [], responsibleAreas: [], conditions: [] };

      const taskAssets = Array.from(
        new Map(
          tasksDeleted
            .map(task => [task.gap?.dependency_control?.dependency?.asset?.name, task.gap?.dependency_control?.dependency?.asset])
            .filter(([_, asset]) => asset && asset.name)
        ).values()
      );

      const taskStatuses = Array.from(
        new Map(
          tasksDeleted
            .map(task => [task.status?.index, task.status])
            .filter(([status]) => status)
        ).values()
      );

      const taskResponsibleAreas = Array.from(
        new Map(
          tasksDeleted
            .map(task => [task.responsible_area?.id, task.responsible_area])
            .filter(([area]) => area)
        ).values()
      );

      const taskConditions = Array.from(
        new Map(
          tasksDeleted
            .map(task => [task.condition, task.condition])
            .filter(([condition]) => condition)
        ).values()
      );

      const taskDependencies = Array.from(
        new Map(
          tasksDeleted
            .map(task => [task.gap?.dependency_control?.dependency?.name, task.gap?.dependency_control?.dependency?.name])
            .filter(([name]) => name)
        ).values()
      );

      const taskTypes = Array.from(
        new Map(
          tasksDeleted
            .map(task => [task.gap?.type, task.gap?.type])
            .filter(([type]) => type)
        ).values()
      );

      const taskAreas = Array.from(
        new Map(
          tasksDeleted
            .map(task => [task.gap?.area?.name, task.gap?.area])
            .filter(([name]) => name)
        ).values()
      );

      return {
        assets: taskAssets.map(asset => ({ name: asset.name })),
        statuses: taskStatuses.map(status => ({ id: status.index, name: status.name })),
        responsibleAreas: taskResponsibleAreas.map(area => ({ id: area.id, name: area.name })),
        conditions: taskConditions.map(name => ({ name })),
        dependencies: taskDependencies.map(name => ({ name })),
        types: taskTypes.map(name => ({ name })),
        areas: taskAreas.map(area => ({ name: area.name })),
      };
  };

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      //hasAnyRole={hasAnyRole}
      requiredPermission="read"
      //currentPermission={canAssetsRead}
      redirectTo="/home"
    >
      <>
        {contextHolder}
        <div className="button-container">
          <Row justify="space-between">
            <Col span={18}>
              <PageTitle title="Tareas Eliminadas" icon="tasks" />
            </Col>
          </Row>

          <Row justify="start" align="top">
            <Col span={18} style={{ display: "flex", alignItems: "center" }}>
              <BackButton onConfirm={goToBioFunctions} />
              <BreadCrumb location={breadCrumbUrl} />
            </Col>
            <Col span={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <TasksSearch
                tasksData={tasksDeleted}
                searchText={searchText}
                setSearchText={setSearchText}
                setFilteredData={setFilteredData}
              />
            </Col>
          </Row>

          <Row justify="end">
            <Col span={6} offset={18} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ExportButton
                api={api}
                contextHolder={contextHolder}
                data={filteredData}
                filename="Activos Eliminados.csv"
                transformData={(data) =>
                  data.map(item => ({
                    Nombre: item.name,
                    Descripción: item.description,
                    Responsable: item.area?.name || 'N/A',
                    "Creado por": item.user?.username || 'N/A',
                  }))
                }
              />
            </Col>
          </Row>
        </div>
      {
        !tasksDeleted ? (
        <>
          <Skeleton active paragraph={{ rows: 6 }} />
          <div style={{ height: 32 }} />
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Col>
            <Col xs={24} md={12}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Col>
          </Row>
        </>
        ) : (
          <>
            <Table
              className="custom-pagination deleted-table "
              columns={Columns(handleOpenChangeLog, tasksDeletedTableFilter())}
              dataSource={filteredData} 
              rowKey="code"
              scroll={{ y: 450 }}
              pagination={{
                defaultPageSize: 30,
                showSizeChanger: true,
                pageSizeOptions: ["30", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} de ${total} registros`,
              }}
            />

            <ChangeLog
              open={changeLogVisible}
              onClose={handleCloseChangeLog}
              data={changeLogData}
              logs={changeLogLogs}
            />
          </>
        )
      }
      </>
    </WithPermission>
  );
};

export default TasksDeleted;